import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { DeleteProject } from "../components/DeleteProject";
import { IssueLicense } from "../components/IssueLicense";
import type { Plan, Project, ProjectOrder } from "../lib/types";
import {
  Badge,
  Button,
  Card,
  DetailRow,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  OrderStatusBadge,
  ProjectStatusBadge,
  Select,
  StatusBadge,
  SubscriptionStatusBadge,
  Table,
  TableWrap,
  Td,
  Th,
  formatDate,
  formatMoney,
} from "../components/ui";

const STATUSES = ["proposed", "in_progress", "active", "suspended", "ended"] as const;

export function ProjectDetailRoute() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const role = user?.role ?? "customer";
  const isAdmin = role === "admin";
  // Issuing a licence is custody, which an operator does not have — and an
  // operator reports as an admin, so `isAdmin` alone would offer it the button.
  // The API refuses it either way; this stops the offer being made.
  const canIssueLicence = isAdmin && user?.canSeeLicensing !== false;
  // Deleting a project is owner-only, the same tier as deleting a licence.
  const isOwner = user?.isOwner === true;
  const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [issuing, setIssuing] = useState(false);

  const project = useQuery({
    queryKey: ["project", id],
    queryFn: () => api<Project>(`/api/v1/admin/projects/${id}`),
  });

  // Its commercial history, which is the reason the entity exists. Fetched
  // separately because a project with none is perfectly ordinary and the detail
  // must render without waiting on it.
  const orders = useQuery({
    queryKey: ["project", id, "orders"],
    queryFn: () => api<ProjectOrder[]>(`/api/v1/admin/projects/${id}/orders`),
  });

  if (project.isPending) return <p className="text-sm text-dim">Loading…</p>;
  if (project.isError) {
    const missing = project.error instanceof ApiError && project.error.status === 404;
    return (
      <div className="space-y-4">
        <ErrorNote>{missing ? "No such project." : "Could not load this project."}</ErrorNote>
        <Link to="/projects" className="text-sm text-accent underline underline-offset-4">
          Back to projects
        </Link>
      </div>
    );
  }

  const data = project.data;

  return (
    <div className="space-y-6">
      <nav>
        <Link to="/projects" className="text-sm text-dim underline-offset-4 hover:underline">
          ← Projects
        </Link>
      </nav>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-1">
          <h1 className="text-xl text-ink">{data.name}</h1>
          <p className="text-sm text-dim">
            <span className="tabular">{data.reference}</span> · {data.customer.name} ·{" "}
            {data.product.name} · {data.plan.name}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <ProjectStatusBadge status={data.status} />
          {data.internal && <Badge tone="quiet">Internal</Badge>}
          {isAdmin && !editing && <Button onClick={() => setEditing(true)}>Edit</Button>}
          {isOwner && (
            <Button variant="danger" onClick={() => setDeleting(true)}>
              Delete
            </Button>
          )}
        </div>
      </header>

      {editing && (
        <EditProject project={data} onClose={() => setEditing(false)} />
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="The project">
          <dl className="divide-y divide-hairline text-sm">
            <DetailRow label="Reference">
              <span className="tabular">{data.reference}</span>
            </DetailRow>
            <DetailRow label="Customer">
              <span className="block">{data.customer.name}</span>
              <span className="block text-xs text-dim">{data.customer.email}</span>
              {data.customer.company != null && (
                <span className="block text-xs text-dim">{data.customer.company}</span>
              )}
            </DetailRow>
            <DetailRow label="Product">
              <span className="block">{data.product.name}</span>
              <span className="block text-xs text-dim">
                {data.product.deliveryType.replace("_", " ")}
              </span>
            </DetailRow>
            <DetailRow label="Plan">
              <span className="block">{data.plan.name}</span>
              <span className="block text-xs text-dim tabular">
                {formatMoney(data.plan.priceCents, data.plan.currency)} / {data.plan.interval}
              </span>
            </DetailRow>
            <DetailRow label="Hosting">
              <Badge tone={data.deployment === "managed" ? "accent" : "quiet"}>
                {data.deployment === "managed" ? "We host it" : "Customer hosts it"}
              </Badge>
            </DetailRow>
            <DetailRow label="Started">{formatDate(data.startedAt)}</DetailRow>
            <DetailRow label="Ended">{formatDate(data.endedAt)}</DetailRow>
            <DetailRow label="Created">{formatDate(data.createdAt)}</DetailRow>
          </dl>
        </Card>

        <div className="space-y-6">
          <Card
            title="Licence"
            action={
              canIssueLicence &&
              data.license === null &&
              data.product.deliveryType === "licensed_software" ? (
                <Button onClick={() => setIssuing((value) => !value)}>
                  {issuing ? "Cancel" : "Issue licence"}
                </Button>
              ) : undefined
            }
          >
            {issuing ? (
              <IssueLicense project={data} onClose={() => setIssuing(false)} />
            ) : data.license === null ? (
              <EmptyState>{noLicenceReason(data)}</EmptyState>
            ) : (
              <dl className="divide-y divide-hairline text-sm">
                <DetailRow label="Key">
                  <Link
                    to={`/licenses/${data.license.id}`}
                    className="tabular text-accent underline-offset-4 hover:underline"
                  >
                    {data.product.code}-••••-{data.license.keyLast4}
                  </Link>
                </DetailRow>
                <DetailRow label="Status">
                  <StatusBadge status={data.license.status} />
                </DetailRow>
                <DetailRow label="Expires">{formatDate(data.license.expiresAt)}</DetailRow>
              </dl>
            )}
          </Card>

          <Card title="Maintenance subscription">
            {data.subscription === null ? (
              <EmptyState>
                Nothing recurring on this project. A subscription opens when an order carrying
                maintenance is paid.
              </EmptyState>
            ) : (
              <dl className="divide-y divide-hairline text-sm">
                <DetailRow label="Status">
                  <SubscriptionStatusBadge status={data.subscription.status} />
                </DetailRow>
                <DetailRow label="Interval">{data.subscription.interval}</DetailRow>
                <DetailRow label={role === "customer" ? "Next charge" : "Paid until"}>
                  {formatDate(data.subscription.currentPeriodEnd)}
                </DetailRow>
              </dl>
            )}
          </Card>

          <Card title="Hostnames">
            {data.hostnames.length === 0 ? (
              <EmptyState>
                No hostnames yet. Add the addresses this runs on as soon as they are known.
              </EmptyState>
            ) : (
              <ul className="divide-y divide-hairline">
                {data.hostnames.map((hostname) => (
                  <li key={hostname} className="px-4 py-2.5 tabular text-sm">
                    {hostname}
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>

      <Card title="Order history">
        {orders.isPending ? (
          <EmptyState>Loading…</EmptyState>
        ) : orders.isError ? (
          <div className="p-4">
            <ErrorNote>Could not load the order history. Reload to try again.</ErrorNote>
          </div>
        ) : orders.data.length === 0 ? (
          <EmptyState>
            No orders against this project yet, which is normal — work often starts before
            anything is invoiced, and some projects are never invoiced at all. Raise one from
            Orders when there is something to charge for, then record how the money arrived.
          </EmptyState>
        ) : (
          <TableWrap>
            <Table>
              <thead className="border-b border-hairline">
                <tr>
                  <Th>Order</Th>
                  <Th>Plan</Th>
                  <Th>Amount</Th>
                  <Th>Status</Th>
                  {role !== "customer" && <Th>Sold by</Th>}
                  <Th>Paid</Th>
                  <Th>Raised</Th>
                </tr>
              </thead>
              <tbody>
                {orders.data.map((order) => (
                  <tr key={order.id} className="border-b border-hairline last:border-0">
                    <Td label="Order">
                      <Link
                        to={`/orders/${order.id}`}
                        className="tabular text-accent underline-offset-4 hover:underline"
                      >
                        {order.number}
                      </Link>
                    </Td>
                    <Td label="Plan" className="text-muted">
                      <span className="block">
                        <span className="block">{order.plan.name}</span>
                        <span className="block text-xs text-dim">{order.plan.interval}</span>
                      </span>
                    </Td>
                    {/* Each order carries its own currency. They are shown one
                        per row and never added together. */}
                    <Td label="Amount" className="tabular">
                      {formatMoney(order.oneTimeCents, order.currency)}
                    </Td>
                    <Td label="Status">
                      <OrderStatusBadge status={order.status} />
                    </Td>
                    {role !== "customer" && (
                      <Td label="Sold by" className="text-muted">
                        {order.marketer === null ? (
                          <span className="text-dim">Direct</span>
                        ) : (
                          order.marketer.name
                        )}
                      </Td>
                    )}
                    <Td label="Paid" className="text-muted">
                      {formatDate(order.paidAt)}
                    </Td>
                    <Td label="Raised" className="text-muted">
                      {formatDate(order.createdAt)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      {isAdmin && data.notes != null && data.notes.length > 0 && (
        <Card title="Notes">
          <p className="whitespace-pre-wrap px-4 py-3 text-sm text-muted">{data.notes}</p>
        </Card>
      )}

      {isOwner && (
        <DeleteProject
          project={data}
          open={deleting}
          onCancel={() => setDeleting(false)}
          onDeleted={() => navigate("/projects")}
        />
      )}
    </div>
  );
}

/**
 * Why there is no key here, which is a different sentence for each reason.
 *
 * The old copy said one arrives "when an order for it is paid", which was not
 * true: plenty of work is paid for by transfer, or invoiced later, or not
 * invoiced at all. Payment is a fact recorded against an order, never a gate on
 * delivery — so the honest answer is that nobody has issued one yet.
 */
function noLicenceReason(project: Project): string {
  return project.product.deliveryType === "licensed_software"
    ? "No licence yet. One is issued automatically when an order is paid, or issue it now if the work is done."
    : "No licence, and none needed — this is delivered as a service rather than as software the customer runs.";
}

/** Everything an admin may correct. The customer and product are fixed for life. */
function EditProject({ project, onClose }: { project: Project; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [delivered, setDelivered] = useState<Project["delivered"]>(null);
  const [name, setName] = useState(project.name);
  const [planId, setPlanId] = useState(project.plan.id);
  const [deployment, setDeployment] = useState(project.deployment);
  const [status, setStatus] = useState(project.status);
  const [internal, setInternal] = useState(project.internal);
  const [hostnames, setHostnames] = useState(project.hostnames.join("\n"));
  const [notes, setNotes] = useState(project.notes ?? "");

  const plans = useQuery({
    queryKey: ["plans", project.product.id],
    queryFn: () => api<Plan[]>(`/api/v1/admin/plans?productId=${project.product.id}`),
  });

  // Until they arrive, the only option is the plan it is already on — an empty
  // select would read as though the project had no plan at all.
  const planOptions: Array<{ id: string; name: string }> = plans.data ?? [project.plan];

  const save = useMutation({
    mutationFn: () =>
      api<Project>(`/api/v1/admin/projects/${project.id}`, {
        method: "PATCH",
        body: {
          name: name.trim(),
          planId,
          deployment,
          status,
          internal,
          hostnames: hostnames
            .split(/[\s,]+/)
            .map((value) => value.trim())
            .filter((value) => value.length > 0),
          notes: notes.trim(),
        },
      }),
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ["project", project.id] });
      void queryClient.invalidateQueries({ queryKey: ["projects"] });

      // Taking a build live mints its key, and this response is the only place
      // it is ever readable. Closing the form on top of it would throw away the
      // one thing the customer needs.
      if (updated.delivered != null) {
        setDelivered(updated.delivered);
        return;
      }
      onClose();
    },
  });

  if (delivered != null) {
    return <Delivered delivered={delivered} onClose={onClose} />;
  }

  return (
    <Card title="Edit project">
      <form
        className="space-y-4 p-4"
        onSubmit={(event) => {
          event.preventDefault();
          save.mutate();
        }}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Name">
            <Input
              value={name}
              required
              maxLength={200}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>

          <Field label="Plan" hint="Only this product's plans. An upgrade is a change here.">
            <Select value={planId} onChange={(event) => setPlanId(event.target.value)}>
              {planOptions.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Deployment">
            <Select
              value={deployment}
              onChange={(event) =>
                setDeployment(event.target.value as Project["deployment"])
              }
            >
              <option value="self_hosted">Customer hosts it</option>
              <option value="managed">We host it</option>
            </Select>
          </Field>

          <Field label="Status" hint="Suspended stops the work, not the record.">
            <Select
              value={status}
              onChange={(event) => setStatus(event.target.value as Project["status"])}
            >
              {STATUSES.map((value) => (
                <option key={value} value={value}>
                  {value.replace("_", " ")}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <label className="flex items-center gap-2 text-sm text-muted">
          <input
            type="checkbox"
            checked={internal}
            onChange={(event) => setInternal(event.target.checked)}
            className="h-4 w-4 rounded border-hairline-strong bg-base accent-accent"
          />
          Internal work — ours, with no customer money behind it
        </label>

        <Field label="Hostnames" hint="One per line. Where this runs.">
          <textarea
            value={hostnames}
            rows={3}
            className="w-full rounded-md border border-hairline-strong bg-base px-3 py-2 tabular text-ink placeholder:text-dim focus:border-accent"
            onChange={(event) => setHostnames(event.target.value)}
          />
        </Field>

        <Field label="Notes">
          <textarea
            value={notes}
            rows={3}
            className="w-full rounded-md border border-hairline-strong bg-base px-3 py-2 text-sm text-ink focus:border-accent"
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>

        {save.isError && <ErrorNote>{(save.error as Error).message}</ErrorNote>}

        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={save.isPending}>
            {save.isPending ? "Saving…" : "Save changes"}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}

/**
 * The key, once. Nothing can recover it afterwards — only its HMAC is stored —
 * so this stays on screen until it is dismissed on purpose rather than closing
 * itself the way a saved form does.
 */
function Delivered({
  delivered,
  onClose,
}: {
  delivered: { licenseKey: string; keyEmailed: boolean };
  onClose: () => void;
}) {
  return (
    <Card title="Delivered">
      <div className="space-y-4 p-4">
        <p className="text-sm text-muted">
          The project is live and its licence key has been issued.
        </p>

        <div className="space-y-1.5">
          <span className="field-label">Licence key</span>
          <code className="block break-all rounded border border-accent/40 bg-accent/5 px-3 py-2.5 tabular text-ink">
            {delivered.licenseKey}
          </code>
          <p className="text-xs text-dim">
            {delivered.keyEmailed
              ? "Emailed to the customer. This is the only time it is shown here."
              : "Not emailed — no mail provider is connected. Copy it now and send it yourself; there is no way to see it again."}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="secondary"
            onClick={() => void navigator.clipboard.writeText(delivered.licenseKey)}
          >
            Copy key
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </Card>
  );
}
